import { Box, Center, Divider, Group, Loader, Stack, Text } from '@mantine/core';
import { Calendar, getTimeRange, TimeGrid } from '@mantine/dates';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';

const TIME_SLOTS = getTimeRange({
  startTime: '09:00',
  endTime: '18:00',
  interval: '00:30',
});

function toDateKey(d: Date | string | dayjs.Dayjs): string {
  return dayjs(d).format('YYYY-MM-DD');
}

function toGridTime(hhmm: string | null): string | null {
  if (!hhmm || !hhmm.trim()) return null;
  return hhmm.length === 5 ? `${hhmm}:00` : hhmm;
}

function fromGridTime(hhmmss: string | null): string | null {
  if (!hhmmss || !hhmmss.trim()) return null;
  return hhmmss.length >= 5 ? hhmmss.slice(0, 5) : hhmmss;
}

/** Seeded mock slots for demo / mock Medplum mode (same algorithm as the picker). */
export function getMockAppointmentTimeSlots(
  dateKey: string,
  availabilityKey?: string,
  count = 5,
): string[] {
  return getAvailableTimeSlots(dateKey, TIME_SLOTS, count, availabilityKey);
}

function getAvailableTimeSlots(
  dateKey: string,
  allSlots: string[],
  count: number,
  availabilityKey?: string
): string[] {
  if (count <= 0) return [];
  if (allSlots.length <= count) return allSlots;

  const seedSource = availabilityKey ? `${availabilityKey}:${dateKey}` : dateKey;
  const seed = seedSource.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const start = seed % allSlots.length;

  const result: string[] = [];
  for (let i = 0; i < count; i += 1) {
    result.push(allSlots[(start + i) % allSlots.length]);
  }
  return result.sort();
}

function getTomorrow(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

export interface AppointmentDateTimePickerProps {
  selectedDate: string | null;
  selectedTime: string | null;
  onDateChange: (dateKey: string) => void;
  onTimeChange: (time: string | null) => void;
  /** Earliest selectable date. Defaults to tomorrow. */
  minDate?: Date;
  /** Latest selectable date (inclusive, start of day). */
  maxDate?: Date;
  /**
   * When set (e.g. episode + provider id), varies which subset of slots appear for a date —
   * simulates provider- and episode-specific availability.
   */
  availabilityKey?: string;
  /**
   * Live mode: function returning HH:mm:ss time slots for a given date key.
   * When provided, overrides the mock slot generation.
   */
  liveTimeSlots?: (dateKey: string) => string[];
  /**
   * Live mode: array of YYYY-MM-DD date keys that have available slots.
   * When provided, only these dates are selectable on the calendar.
   */
  availableDates?: string[];
  /**
   * Label for which timezone slot times are in (display only).
   * @default Pacific Time (PT)
   */
  timeZoneLabel?: string;
  /** When true, shows a spinner over the time grid without changing layout height. */
  timesLoading?: boolean;
  /** When true, shows a spinner over the calendar while bookable dates are loading. */
  datesLoading?: boolean;
  /** Shown inside the time panel when a date is selected but no slots are available (live mode). */
  emptyTimeSlotsMessage?: string | null;
}

export function AppointmentDateTimePicker({
  selectedDate,
  selectedTime,
  onDateChange,
  onTimeChange,
  minDate = getTomorrow(),
  maxDate,
  availabilityKey,
  liveTimeSlots,
  availableDates,
  timeZoneLabel = 'Pacific Time (PT)',
  timesLoading = false,
  datesLoading = false,
  emptyTimeSlotsMessage = null,
}: AppointmentDateTimePickerProps): JSX.Element {
  const timeValue = toGridTime(selectedTime);
  const minDay = dayjs(minDate).startOf('day');
  const maxDay = maxDate ? dayjs(maxDate).startOf('day') : null;
  const hasSelectedDate = Boolean(selectedDate) && !dayjs(selectedDate).isBefore(minDay, 'day');
  const availableTimeSlots = useMemo(() => {
    if (!selectedDate) return [];
    if (liveTimeSlots) return liveTimeSlots(selectedDate);
    return getAvailableTimeSlots(selectedDate, TIME_SLOTS, 5, availabilityKey);
  }, [selectedDate, availabilityKey, liveTimeSlots]);

  const calendarDisplayDate = selectedDate ? dayjs(selectedDate).toDate() : dayjs().startOf('day').toDate();
  const calendarMonthKey = `${calendarDisplayDate.getFullYear()}-${calendarDisplayDate.getMonth()}`;

  const calendarWrapRef = useRef<HTMLDivElement>(null);
  const [timePanelMaxHeight, setTimePanelMaxHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = calendarWrapRef.current;
    if (!el) return;

    const syncHeight = () => {
      setTimePanelMaxHeight(el.offsetHeight);
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [calendarMonthKey]);

  const showEmptySlotsMessage =
    Boolean(emptyTimeSlotsMessage) &&
    hasSelectedDate &&
    !timesLoading &&
    availableTimeSlots.length === 0 &&
    Boolean(liveTimeSlots);

  return (
    <Group align="flex-start" gap="lg" wrap="wrap">
      <Box ref={calendarWrapRef} style={{ flexShrink: 0, position: 'relative' }}>
      <Calendar
        date={calendarDisplayDate}
        weekendDays={[]}
        minDate={minDate}
        maxDate={maxDate}
        onDateChange={(d) => {
          if (!d) return;
          onDateChange(toDateKey(d));
          onTimeChange(null);
        }}
        getDayProps={(date) => {
          const key = toDateKey(date);
          const selected = selectedDate === key;
          const beforeMin = dayjs(date).startOf('day').isBefore(minDay, 'day');
          const afterMax = maxDay ? dayjs(date).startOf('day').isAfter(maxDay, 'day') : false;
          const unavailableInList = availableDates ? !availableDates.includes(key) : false;
          const disabled = datesLoading || beforeMin || afterMax || unavailableInList;
          return {
            selected,
            disabled,
            onClick: () => {
              if (disabled) return;
              onDateChange(key);
              onTimeChange(null);
            },
          };
        }}
        size="md"
      />
      {datesLoading ? (
        <Center
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.72)',
            pointerEvents: 'none',
          }}
        >
          <Loader size="sm" color="gold" />
        </Center>
      ) : null}
      </Box>
      <Divider orientation="vertical" />
      <Stack
        gap="xs"
        style={{
          flex: 1,
          minWidth: 140,
          maxHeight: timePanelMaxHeight,
          overflow: 'hidden',
        }}
      >
        <Box style={{ flexShrink: 0 }}>
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Time
            </Text>
            {timeZoneLabel ? (
              <Text size="xs" c="dimmed">
                Times are in {timeZoneLabel}.
              </Text>
            ) : null}
          </Stack>
          <Text size="xs" c="dimmed" mt={4}>
            {hasSelectedDate
              ? 'Times shown reflect your care team availability for this episode.'
              : 'Select a date first to enable time selection.'}
          </Text>
        </Box>
        <Box
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            paddingRight: 4,
            position: 'relative',
          }}
        >
          <fieldset
            disabled={!hasSelectedDate || timesLoading}
            style={{
              border: 0,
              padding: 0,
              margin: 0,
              minInlineSize: 0,
              opacity: hasSelectedDate && !timesLoading ? 1 : 0.55,
            }}
          >
            <TimeGrid
              value={timeValue}
              onChange={(value) => onTimeChange(fromGridTime(value ?? null))}
              data={availableTimeSlots}
              format="12h"
              size="xs"
              simpleGridProps={{ type: 'container', cols: 2, spacing: 'xs' }}
            />
          </fieldset>
          {showEmptySlotsMessage ? (
            <Center
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                padding: '0 8px',
              }}
            >
              <Text size="sm" c="dimmed" ta="center">
                {emptyTimeSlotsMessage}
              </Text>
            </Center>
          ) : null}
          {timesLoading ? (
            <Center
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
              }}
            >
              <Loader size="sm" color="gold" />
            </Center>
          ) : null}
        </Box>
      </Stack>
    </Group>
  );
}
